/**
 * Cluster Tool
 *
 * Allows creating and analyzing point clusters by selecting point cloud segments.
 * Supports single-click segment selection and ctrl+drag lasso selection.
 * Forces 3D perspective view for optimal cluster visualization.
 */

import { BaseTool, DragEventData } from '../../BaseTool';
import { Viewer } from '../../../viewer/viewer';
import { IPersona, IViewConfig } from '../../types';
import { ViewModes } from '../../helpers';
import { Snapshot } from '../ToolUndoRedoManager';
import { PointCluster } from '../../../utils/PointCluster';
import { OverlayLine } from '../../../utils/OverlayLine';
import { ClipTask, DefaultColors } from '../../../defines';
import { Utils } from '../../../utils';
import * as THREE from 'three';
// @ts-expect-error — global from apps\client\index.html
declare const turf;

// ─── Snapshot for segment changes ────────────────────────────────

class ClusterSegmentsSnapshot implements Snapshot {
  private segments: { pointcloudId: string; segmentId: number }[];

  constructor(
    private readonly cluster: PointCluster,
    segments: { pointcloudId: string; segmentId: number }[],
  ) {
    this.segments = [...segments];
  }

  restore(): Snapshot {
    const currentSegments = [...this.cluster.segments];
    this.cluster.segments = this.segments;
    this.segments = currentSegments;
    return this;
  }
}

/** Shape of the pick-result point returned by PointCloudOctree.pick() at runtime. */
interface PickPointResult {
  pointCloudId?: string;
  attributes: Record<string, THREE.Vector3 | number[]>;
}

/**
 * Extract pointCloudId and first segment id from a pick intersection.
 * The runtime pick result contains geometry attributes (including 'segment')
 * on point.attributes, plus pointCloudId directly on the point object.
 */
function extractSegmentInfo(I: ReturnType<typeof Utils.getMousePointCloudIntersection>): { id: string; segment: number } | null {
  const point = I && 'point' in I ? (I.point as unknown as PickPointResult) : undefined;
  const id = point?.pointCloudId;
  const segmentAttr = point?.attributes?.['segment'];
  const segment = Array.isArray(segmentAttr) ? (segmentAttr as number[])[0] : undefined;
  if (!id || segment === undefined) return null;
  return { id, segment };
}

export class ClusterPersonaTool extends BaseTool {
  readonly name = 'cluster';

  private currentPointCluster: PointCluster | undefined;
  private hoveredCluster: PointCluster;
  private ctrlDown = false;
  private lassoActive = false;
  private overlayLine: OverlayLine | null = null;
  private dragPoints: THREE.Vector2[] = [];
  constructor(viewer: Viewer, persona: IPersona) {
    super(viewer, persona);
    this.hoveredCluster = new PointCluster({});
    this.initUndoRedo();
  }

  // ─── Lifecycle ──────────────────────────────────────────────────────

  protected onEnable(): void {
    if (this.viewer.orbitControls) {
      this.viewer.orbitControls.doubleClickZoomEnabled = false;
    }
    this.overlayLine = new OverlayLine({ color: '#' + new THREE.Color(DefaultColors.OBJECTS).getHexString() });
    this.startNewPointCluster();
  }

  protected onDisable(): void {
    if (this.currentPointCluster && !this.currentPointCluster.finished) {
      this.viewer.scene.removePointCluster(this.currentPointCluster);
    }
    this.setCurrentPointCluster(undefined);

    this.ctrlDown = false;
    this.lassoActive = false;
    this.dragPoints = [];

    if (this.overlayLine) {
      this.overlayLine.dispose();
      this.overlayLine = null;
    }

    if (this.viewer.orbitControls) {
      this.viewer.orbitControls.doubleClickZoomEnabled = true;
    }

    Utils.changeCursor('auto');
    this.viewer.inputHandler.unblockOrbitControls();
  }

  getViewConfig(): IViewConfig {
    return {
      views: [ViewModes.PERSPECTIVE_3D_RGB],
      defaultViewId: 'perspective_3d_rgb',
    };
  }

  // ─── Cluster Management ─────────────────────────────────────────────

  private setCurrentPointCluster(pointCluster: PointCluster | undefined): void {
    this.hoveredCluster.segments = [];
    this.currentPointCluster = pointCluster;
  }

  private pushSegmentSnapshot(): void {
    if (!this.currentPointCluster) return;
    this.undoRedo?.pushState(new ClusterSegmentsSnapshot(this.currentPointCluster, [...this.currentPointCluster.segments]));
  }

  private startNewPointCluster(): void {
    const newCluster = new PointCluster({});
    this.setCurrentPointCluster(newCluster);
  }

  public finishPointCluster(): void {
    if (!this.currentPointCluster) return;
    if (!this.currentPointCluster.segments.length) return;

    this.undoRedo?.clear();
    this.currentPointCluster.assignedClipTask = ClipTask.NONE;

    if (!this.currentPointCluster.finished) {
      this.currentPointCluster.finish();

      this.viewer.scene.addPointCluster(this.currentPointCluster);
      this.viewer.scene.dispatchEvent({
        type: 'pointorama_pointCluster_added',
        value: { pointCluster: this.currentPointCluster },
      });
    }

    this.startNewPointCluster();
  }

  private editPointCluster(): void {
    if (this.currentPointCluster?.finished) {
      this.viewer.scene.dispatchEvent({
        type: 'pointorama_pointCluster_edit',
        value: { pointCluster: this.currentPointCluster },
      });
    }
  }

  private segmentIsClustered(pointcloudId: string, segmentId: number) {
    return this.viewer.scene.pointClusters.find((cluster) => cluster.containsSegment(pointcloudId, segmentId));
  }

  // ─── Lasso ──────────────────────────────────────────────────────────

  private startLasso(): void {
    this.lassoActive = true;
    this.dragPoints = [];
    this.overlayLine?.clearPoints();
    this.viewer.inputHandler.blockOrbitControls();
  }

  private endLasso(): void {
    if (!this.lassoActive) return;

    this.lassoActive = false;
    this.overlayLine?.clearPoints();

    if (this.dragPoints.length >= 3) {
      this.pushSegmentSnapshot();
      const lassoPolygon = turf.simplify(Utils.toTurfObject(this.dragPoints), { tolerance: 5, highQuality: false });
      const results = Utils.getPolygonPointCloudIntersection(lassoPolygon, this.viewer.scene.getActiveCamera(), this.viewer, this.viewer.scene.pointclouds, { threshold: 0.65 });
      results.forEach((result) => {
        if (!result.pointcloudId) return;
        const pointcloudId = result.pointcloudId;
        result.segmentIdList.forEach((segmentId) => {
          const cluster = this.segmentIsClustered(pointcloudId, segmentId);
          if (!cluster) {
            this.currentPointCluster?.addSegment(pointcloudId, segmentId);
          }
        });
      });
    }

    Utils.changeCursor('auto');
    this.viewer.inputHandler.unblockOrbitControls();
  }

  // ─── Rendering Integration ──────────────────────────────────────────

  public getClustersForPointCloud(id: string) {
    const clusters = [];

    const currentSegmentsForPointCloud = this.currentPointCluster?.segments.filter((segment) => segment.pointcloudId === id);
    if (currentSegmentsForPointCloud?.length) {
      const segments = currentSegmentsForPointCloud.map((segment) => ({
        identifier: segment.segmentId,
        clipTask: ClipTask.HIGHLIGHT,
        classification: -1,
        visible: true,
        color: this.currentPointCluster?.color,
      }));
      clusters.push(...segments);
    }

    const hoveredSegmentsForPointCloud = this.hoveredCluster.segments.filter((segment) => segment.pointcloudId === id);
    if (hoveredSegmentsForPointCloud.length) {
      const segments = hoveredSegmentsForPointCloud.map((segment) => ({
        identifier: segment.segmentId,
        clipTask: ClipTask.HIGHLIGHT,
        classification: -1,
        visible: true,
        color: new THREE.Color(DefaultColors.HOVER),
      }));
      clusters.push(...segments);
    }

    return clusters;
  }

  // ─── Input Handlers ─────────────────────────────────────────────────

  public onMouseMove(_event: MouseEvent): void {
    if (this.lassoActive) return;

    const mouse = this.viewer.inputHandler.mouse;
    const I = Utils.getMousePointCloudIntersection(mouse, this.viewer.scene.getActiveCamera(), this.viewer, this.viewer.scene.pointclouds, { pickClipped: false });
    const info = extractSegmentInfo(I);

    if (info) {
      const { id, segment } = info;
      if (!this.currentPointCluster?.containsSegment(id, segment)) {
        const cluster = this.segmentIsClustered(id, segment);
        if (cluster) {
          // Don't allow users to activate clusters while they're in the process of creating/editing another cluster.
          if (this.currentPointCluster?.segments.length) {
            this.hoveredCluster.segments = [];
          } else {
            this.hoveredCluster.segments = cluster.segments;
          }
        } else {
          this.hoveredCluster.segments = [{ pointcloudId: id, segmentId: segment }];
        }
      } else {
        this.hoveredCluster.segments = [{ pointcloudId: id, segmentId: segment }];
      }
    } else {
      this.hoveredCluster.segments = [];
    }
  }

  public onClick(_event: MouseEvent): void {
    if (!this.currentPointCluster) return;

    const mouse = this.viewer.inputHandler.mouse;
    const I = Utils.getMousePointCloudIntersection(mouse, this.viewer.scene.getActiveCamera(), this.viewer, this.viewer.scene.pointclouds, { pickClipped: false });
    const info = extractSegmentInfo(I);
    if (!info) return;
    const { id, segment } = info;

    if (this.currentPointCluster.containsSegment(id, segment)) {
      // Left-click on existing segment does nothing
      return;
    }

    const cluster = this.segmentIsClustered(id, segment);
    if (cluster) {
      // Don't allow users to activate clusters while they're in the process of creating/editing another cluster.
      if (!this.currentPointCluster?.segments.length) this.setCurrentPointCluster(cluster);
    } else {
      this.pushSegmentSnapshot();
      this.currentPointCluster.addSegment(id, segment);
      if (this.currentPointCluster.finished) this.editPointCluster();
    }
  }

  public onRightClick(_event: MouseEvent): void {
    if (!this.currentPointCluster) return;

    const mouse = this.viewer.inputHandler.mouse;
    const I = Utils.getMousePointCloudIntersection(mouse, this.viewer.scene.getActiveCamera(), this.viewer, this.viewer.scene.pointclouds, { pickClipped: false });
    const info = extractSegmentInfo(I);
    if (!info) return;
    const { id, segment } = info;

    if (this.currentPointCluster.containsSegment(id, segment)) {
      if (this.currentPointCluster.finished && this.currentPointCluster.segments.length === 1) {
        // When last segment is removed from finished cluster, delete the cluster.
        this.viewer.scene.dispatchEvent({
          type: 'measurement_delete',
          value: { measure: this.currentPointCluster },
        });
        this.startNewPointCluster();
      } else {
        this.pushSegmentSnapshot();
        this.currentPointCluster.removeSegment(id, segment);
        if (this.currentPointCluster.finished) this.editPointCluster();
      }
    }
  }

  public onDoubleClick(_event: MouseEvent): void {
    this.finishPointCluster();
  }

  public onMouseDown(event: MouseEvent): void {
    // Sync ctrl state in case ctrl was pressed while browser was unfocused
    if (event.ctrlKey && !this.ctrlDown) {
      this.ctrlDown = true;
    }
  }

  public onDragStart(data: DragEventData): void {
    if (this.ctrlDown && data.button === 0) {
      this.startLasso();
    }
  }

  public onDrag(data: DragEventData): void {
    if (!this.lassoActive) return;
    const point = data.currentScreen.clone();
    this.dragPoints.push(point);
    this.overlayLine?.addPoint(point);
    this.overlayLine?.draw();
  }

  public onDragEnd(_data: DragEventData): void {
    if (!this.lassoActive) return;
    this.endLasso();
  }

  public onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Control') {
      this.ctrlDown = true;
      if (this.viewer.orbitControls.mode !== 'DRAG' && this.viewer.orbitControls.mode !== 'PAN') {
        Utils.changeCursor('lasso');
      }
    } else if (event.key === 'Enter') {
      this.finishPointCluster();
    } else if (event.key === 'Escape') {
      if (this.currentPointCluster && !this.currentPointCluster.finished && this.currentPointCluster.segments.length) {
        this.undoRedo?.clear();
        this.viewer.scene.removePointCluster(this.currentPointCluster);
        this.startNewPointCluster();
      }
    }
  }

  public onKeyUp(event: KeyboardEvent): void {
    if (event.key === 'Control') {
      this.ctrlDown = false;
      this.endLasso();
    }
  }
}
